import { DutyItem } from '@app/Models/WebApiModels';
import { Range } from '@app/Components/VirtualizedRoster';
import DateTimeService from './DateTimeService';

export type DutyChangeEvent = {
    duties?: DutyItem[];
    personsRange?: {
        personIds: string[];
        range: Range<Date>;
    };
    dutyRange: Range<Date> | null;
};

export type DutyChangeHandler = (changes: DutyChangeEvent) => Promise<void>;

class DutyChangeService {
    private _handlerList: DutyChangeHandler[] = [];

    public notifyDutyChange(duties: DutyItem[]): Promise<void[]> {
        const handlers = this._handlerList.slice();

        let minUpdateDate: Date | null = null;
        let maxUpdateDate: Date | null = null;
        const personIds: string[] = [];
        duties.forEach((d) => {
            minUpdateDate = DateTimeService.min(minUpdateDate, d.date);
            maxUpdateDate = DateTimeService.max(maxUpdateDate, d.date);
            if (personIds.indexOf(d.personId) === -1) personIds.push(d.personId);
        });

        const updates: Promise<void>[] = [];
        for (let i = 0; i < handlers.length; i++) {
            const result = handlers[i]({
                duties: duties,
                dutyRange: minUpdateDate && maxUpdateDate && { from: minUpdateDate, to: maxUpdateDate }
            });
            result && updates.push(result);
        }

        return Promise.all(updates);
    }

    public notifyRangeChange(range: Range<Date>, personIds: string[]): Promise<void[]> {
        const handlers = this._handlerList.slice();
        const updates: Promise<void>[] = [];
        for (let i = 0; i < handlers.length; i++) {
            const result = handlers[i]({
                personsRange: {
                    range: range,
                    personIds: personIds
                },
                dutyRange: range
            });
            result && updates.push(result);
        }

        return Promise.all(updates);
    }

    public subscribe(onChange: DutyChangeHandler) {
        if (this._handlerList.indexOf(onChange) !== -1) {
            throw new Error('Callback is already registered!');
        }
        this._handlerList.push(onChange);
    }

    public unsubscribe(onChange: DutyChangeHandler) {
        const callbackIndex = this._handlerList.indexOf(onChange);
        if (callbackIndex === -1) throw new Error('Callback is not registered!');

        this._handlerList.splice(callbackIndex, 1);
    }
}

export const dutyChangeService = new DutyChangeService();
